import React, { useEffect, useState } from "react";
import AppShell from "@/components/AppShell";
import { api } from "@/lib/api";
import { useLocation } from "@/lib/location";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Plus, Trash, PencilSimple, CaretUp, CaretDown } from "@phosphor-icons/react";
import { toast } from "sonner";
import { PhosphorIcon } from "@/components/PhosphorIcon";

const PRIORITY_OPTIONS = ["low", "normal", "high", "urgent"];
const EMPTY = { name: "", icon: "Coffee", department_id: "", color: "#0055FF", priority: "normal", group: "Hospitality", active: true, display_order: 0 };

export default function AdminCategories() {
    const { activeLocationId, requireLocation } = useLocation() || {};
    const [cats, setCats] = useState([]);
    const [depts, setDepts] = useState([]);
    const [showDialog, setShowDialog] = useState(false);
    const [editing, setEditing] = useState(null);   // category being edited, or null for create
    const [form, setForm] = useState(EMPTY);
    const [groupMode, setGroupMode] = useState("existing");
    const [newGroup, setNewGroup] = useState("");

    const load = async () => {
        const [c, d] = await Promise.all([api.get("/categories"), api.get("/departments")]);
        setCats(c.data);
        setDepts(d.data);
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [activeLocationId]);

    const openCreate = () => {
        setEditing(null);
        setForm({ ...EMPTY, display_order: cats.length });   // append to end by default
        setGroupMode("existing");
        setShowDialog(true);
    };
    const openEdit = (cat) => {
        setEditing(cat);
        setForm({
            name: cat.name, icon: cat.icon, department_id: cat.department_id, color: cat.color,
            priority: cat.priority, group: cat.group, active: cat.active,
            display_order: cat.display_order ?? 0,
        });
        setGroupMode("existing");
        setShowDialog(true);
    };

    const submit = async () => {
        if (!form.name || !form.department_id) return toast.error("Name and department required");
        const finalGroup = groupMode === "new" ? newGroup.trim() : form.group;
        if (!finalGroup) return toast.error("Group is required");
        const payload = { ...form, group: finalGroup };
        try {
            if (editing) {
                await api.patch(`/categories/${editing.id}`, payload);
                toast.success("Saved");
            } else {
                await api.post("/categories", payload);
                toast.success("Tap-to-Request item created");
            }
            setShowDialog(false);
            setEditing(null);
            setForm(EMPTY);
            setGroupMode("existing");
            setNewGroup("");
            load();
        } catch { toast.error("Failed"); }
    };

    const toggle = async (cat) => {
        try {
            await api.patch(`/categories/${cat.id}`, { active: !cat.active });
            load();
        } catch { toast.error("Failed"); }
    };

    const remove = async (id) => {
        if (!window.confirm("Delete this Tap-to-Request item?")) return;
        try {
            await api.delete(`/categories/${id}`);
            toast.success("Deleted");
            load();
        } catch { toast.error("Failed"); }
    };

    // Move a category up/down inside its group by swapping display_order values
    // with its neighbour. Saves both rows with parallel PATCH requests. Cheap
    // and avoids a dedicated reorder endpoint.
    const move = async (cat, dir) => {
        const peers = cats
            .filter((c) => c.group === cat.group)
            .slice()
            .sort((a, b) => (a.display_order ?? 0) - (b.display_order ?? 0) || a.created_at.localeCompare(b.created_at));
        const idx = peers.findIndex((c) => c.id === cat.id);
        const target = dir === "up" ? idx - 1 : idx + 1;
        if (target < 0 || target >= peers.length) return;
        const a = peers[idx], b = peers[target];
        try {
            await Promise.all([
                api.patch(`/categories/${a.id}`, { display_order: b.display_order ?? target }),
                api.patch(`/categories/${b.id}`, { display_order: a.display_order ?? idx }),
            ]);
            load();
        } catch { toast.error("Reorder failed"); }
    };

    // Group + sort by display_order so the admin preview matches what the
    // kiosk actually renders.
    const grouped = cats.reduce((acc, c) => {
        (acc[c.group] = acc[c.group] || []).push(c);
        return acc;
    }, {});
    Object.values(grouped).forEach((arr) =>
        arr.sort((x, y) => (x.display_order ?? 0) - (y.display_order ?? 0) || x.created_at.localeCompare(y.created_at))
    );

    const existingGroups = Array.from(new Set(cats.map((c) => c.group).filter(Boolean))).sort();

    return (
        <AppShell
            title="Tap to Request"
            subtitle="Manage the request tiles shown on each room kiosk"
            actions={
                <>
                    <Button
                        onClick={() => requireLocation && requireLocation(openCreate, "a Tap-to-Request item")}
                        className="bg-[#0055FF] hover:bg-[#0044CC] text-white"
                        data-testid="categories-create-btn"
                    >
                        <Plus size={16} className="mr-1" /> New item
                    </Button>
                    <Dialog open={showDialog} onOpenChange={setShowDialog}>
                        <DialogContent>
                        <DialogHeader><DialogTitle>{editing ? "Edit item" : "New Tap-to-Request item"}</DialogTitle></DialogHeader>
                        <div className="space-y-3">
                            <div>
                                <Label>Name</Label>
                                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="cat-name-input" />
                            </div>
                            <div>
                                <Label>Group</Label>
                                <div className="flex items-center gap-2">
                                    {groupMode === "existing" ? (
                                        <Select value={form.group} onValueChange={(v) => setForm({ ...form, group: v })}>
                                            <SelectTrigger className="flex-1" data-testid="cat-group-select"><SelectValue placeholder="Pick a group" /></SelectTrigger>
                                            <SelectContent>
                                                {existingGroups.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}
                                            </SelectContent>
                                        </Select>
                                    ) : (
                                        <Input value={newGroup} onChange={(e) => setNewGroup(e.target.value)} placeholder="New group name" className="flex-1" />
                                    )}
                                    <Button variant="ghost" size="sm" onClick={() => setGroupMode((m) => m === "existing" ? "new" : "existing")}>
                                        {groupMode === "existing" ? "+ Add new" : "Use existing"}
                                    </Button>
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <Label>Phosphor Icon name</Label>
                                    <Input value={form.icon} onChange={(e) => setForm({ ...form, icon: e.target.value })} placeholder="Coffee" data-testid="cat-icon-input" />
                                </div>
                                <div>
                                    <Label>Display order</Label>
                                    <Input
                                        type="number"
                                        value={form.display_order}
                                        onChange={(e) => setForm({ ...form, display_order: Number(e.target.value) || 0 })}
                                        data-testid="cat-order-input"
                                    />
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <Label>Color</Label>
                                    <Input type="color" value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} />
                                </div>
                                <div>
                                    <Label>Priority</Label>
                                    <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                                        <SelectTrigger><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            {PRIORITY_OPTIONS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>
                            <div>
                                <Label>Department</Label>
                                <Select value={form.department_id} onValueChange={(v) => setForm({ ...form, department_id: v })}>
                                    <SelectTrigger data-testid="cat-dept-select"><SelectValue placeholder="Select department" /></SelectTrigger>
                                    <SelectContent>
                                        {depts.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>
                            <Button onClick={submit} className="w-full bg-[#0055FF] hover:bg-[#0044CC] text-white" data-testid="cat-create-submit-btn">
                                {editing ? "Save changes" : "Create"}
                            </Button>
                        </div>
                    </DialogContent>
                </Dialog>
                </>
            }
        >
            <div className="p-6 space-y-8">
                {Object.entries(grouped).map(([group, items]) => (
                    <section key={group}>
                        <div className="label-eyebrow mb-3">{group}</div>
                        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
                            {items.map((c, idx) => (
                                <Card key={c.id} className="border-gray-200 bg-white p-4 flex items-center gap-3" data-testid={`cat-card-${c.id}`}>
                                    <div className="flex flex-col">
                                        <Button variant="ghost" size="icon" className="h-6 w-6 -mb-0.5" disabled={idx === 0} onClick={() => move(c, "up")} data-testid={`cat-${c.id}-up`}>
                                            <CaretUp size={12} />
                                        </Button>
                                        <Button variant="ghost" size="icon" className="h-6 w-6" disabled={idx === items.length - 1} onClick={() => move(c, "down")} data-testid={`cat-${c.id}-down`}>
                                            <CaretDown size={12} />
                                        </Button>
                                    </div>
                                    <div className="w-11 h-11 rounded-lg grid place-items-center" style={{ backgroundColor: `${c.color}15` }}>
                                        <PhosphorIcon name={c.icon} size={22} weight="duotone" color={c.color} />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="font-medium text-gray-900 truncate">{c.name}</div>
                                        <div className="text-xs text-gray-500 capitalize">{c.priority} · order {c.display_order ?? 0}</div>
                                    </div>
                                    <Switch checked={c.active} onCheckedChange={() => toggle(c)} data-testid={`cat-${c.id}-toggle`} />
                                    <Button size="sm" variant="ghost" onClick={() => openEdit(c)} className="text-gray-600" data-testid={`cat-${c.id}-edit`}>
                                        <PencilSimple size={14} />
                                    </Button>
                                    <Button size="sm" variant="ghost" onClick={() => remove(c.id)} className="text-red-600 hover:bg-red-50" data-testid={`cat-${c.id}-delete`}>
                                        <Trash size={14} />
                                    </Button>
                                </Card>
                            ))}
                        </div>
                    </section>
                ))}
            </div>
        </AppShell>
    );
}
